import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import "./Dashboard.css";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const ALL_DATES_API  = "https://api.chefcoco.com/app/admin/all/dates";
const BLACKLIST_API  = "https://api.chefcoco.com/app/admin/blacklist/date";
const WHITELIST_API  = "https://api.chefcoco.com/app/admin/whitelist/date";

function toKey(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function getEarliestLockableWed(today) {
  const dayOfWeek = today.getDay();
  const daysUntilThisWed = (3 - dayOfWeek + 7) % 7;
  const thisWed = new Date(today);
  thisWed.setHours(0, 0, 0, 0);
  thisWed.setDate(today.getDate() + daysUntilThisWed);
  const nextWed = new Date(thisWed);
  nextWed.setDate(thisWed.getDate() + 7);
  return nextWed;
}

function isWednesday(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.getDay() === 3;
}

function Toast({ message, visible, type = "default" }) {
  const bgColor = type === "success" ? "#16a34a" : type === "error" ? "#dc2626" : "#1a1a1a";
  const icon    = type === "success" ? "✅" : type === "error" ? "❌" : "🔒";
  return (
    <div style={{
      position: "fixed", bottom: "32px", left: "50%",
      transform: `translateX(-50%) translateY(${visible ? "0" : "16px"})`,
      opacity: visible ? 1 : 0,
      transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
      background: bgColor, color: "#fff",
      padding: "10px 20px", borderRadius: "10px",
      fontSize: "13px", fontWeight: 500,
      pointerEvents: "none", zIndex: 9999,
      boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
      display: "flex", alignItems: "center", gap: "8px",
    }}>
      <span style={{ fontSize: "15px" }}>{icon}</span>
      {message}
    </div>
  );
}

function BlockModal({ date, onConfirm, onClose, loading }) {
  if (!date) return null;
  const label = date.toLocaleDateString("en-US", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        zIndex: 10000, backdropFilter: "blur(2px)", animation: "fadeIn 0.18s ease",
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", zIndex: 10001,
        background: "#fff", borderRadius: "18px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", width: "320px",
        overflow: "hidden", animation: "popIn 0.22s cubic-bezier(.34,1.56,.64,1)",
      }}>
        <div style={{ background: "#fff1f0", padding: "22px 20px 16px", borderBottom: "1px solid #fee2e2", textAlign: "center" }}>
          <div style={{ fontSize: "38px", marginBottom: "6px" }}>🔒</div>
          <div style={{ fontSize: "15px", fontWeight: 700, color: "#1a1a1a" }}>Block Delivery</div>
        </div>
        <div style={{ padding: "18px 20px 8px" }}>
          <div style={{
            background: "#f5f5f5", borderRadius: "10px", padding: "10px 14px",
            display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px",
          }}>
            <span style={{ fontSize: "20px" }}>📅</span>
            <div>
              <div style={{ fontSize: "11px", color: "#888", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" }}>Selected day</div>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a1a", marginTop: "2px" }}>{label}</div>
            </div>
          </div>
          <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.55, margin: "0 0 14px" }}>
            Blocking this day will prevent all deliveries on this day.
          </p>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
            background: "#f0fdf4", color: "#16a34a", marginBottom: "18px",
          }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
            Currently Active
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px", padding: "0 20px 20px" }}>
          <button onClick={onClose} disabled={loading} style={{
            flex: 1, padding: "11px", borderRadius: "10px",
            border: "1.5px solid #e5e7eb", background: "#fff",
            color: "#555", fontSize: "13px", fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{
            flex: 1, padding: "11px", borderRadius: "10px",
            border: "none", background: loading ? "#f87171" : "#e8372c",
            color: "#fff", fontSize: "13px", fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 14px #e8372c55",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
          }}>
            {loading ? <Spinner /> : null}
            {loading ? "Blocking…" : "Block Delivery"}
          </button>
        </div>
      </div>
    </>
  );
}

function UnblockModal({ date, isBlocked, onConfirm, onClose, loading }) {
  if (!date) return null;
  const label = date.toLocaleDateString("en-US", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        zIndex: 10000, backdropFilter: "blur(2px)", animation: "fadeIn 0.18s ease",
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", zIndex: 10001,
        background: "#fff", borderRadius: "18px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", width: "320px",
        overflow: "hidden", animation: "popIn 0.22s cubic-bezier(.34,1.56,.64,1)",
      }}>
        <div style={{
          background: "linear-gradient(135deg, #065f46 0%, #047857 100%)",
          padding: "22px 20px 18px", textAlign: "center",
        }}>
          <div style={{
            width: "60px", height: "60px", borderRadius: "50%",
            background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 10px", fontSize: "28px",
          }}>🔓</div>
          <div style={{ fontSize: "16px", fontWeight: 700, color: "#fff" }}>Remove Block</div>
          <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.65)", marginTop: "3px" }}>Re-enable deliveries for this date</div>
        </div>
        <div style={{ padding: "18px 20px 0" }}>
          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "12px",
            padding: "12px 14px", display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px",
          }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "10px", background: "#dcfce7",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0,
            }}>📅</div>
            <div>
              <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Blocked day
              </div>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a1a", marginTop: "2px" }}>{label}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "5px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
              background: "#fee2e2", color: "#dc2626",
            }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
              Blocked
            </div>
            <span style={{ fontSize: "16px", color: "#9ca3af" }}>→</span>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "5px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
              background: "#dcfce7", color: "#16a34a",
            }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
              Active
            </div>
          </div>
          <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.55, margin: "0 0 14px" }}>
            Removing this block will re-enable all customer deliveries for this day.
          </p>
        </div>
        <div style={{
          margin: "0 20px 16px", background: "#fffbeb", border: "1px solid #fde68a",
          borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#92400e",
          display: "flex", gap: "8px", alignItems: "flex-start",
        }}>
          <span style={{ fontSize: "14px", flexShrink: 0 }}>⚠️</span>
          <span>Customers will immediately be able to place orders for this day once unblocked.</span>
        </div>
        <div style={{ display: "flex", gap: "10px", padding: "0 20px 20px" }}>
          <button onClick={onClose} disabled={loading} style={{
            flex: 1, padding: "11px", borderRadius: "10px",
            border: "1.5px solid #e5e7eb", background: "#fff",
            color: "#555", fontSize: "13px", fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
          }}>Keep Blocked</button>
          <button onClick={onConfirm} disabled={loading} style={{
            flex: 1, padding: "11px", borderRadius: "10px", border: "none",
            background: loading ? "#4ade80" : "linear-gradient(135deg, #065f46 0%, #16a34a 100%)",
            color: "#fff", fontSize: "13px", fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 14px rgba(22,163,74,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
          }}>
            {loading ? <Spinner /> : null}
            {loading ? "Removing…" : "Remove Block"}
          </button>
        </div>
      </div>
    </>
  );
}

function Spinner() {
  return (
    <span style={{
      display: "inline-block", width: "13px", height: "13px",
      border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
      borderRadius: "50%", animation: "spin 0.6s linear infinite", flexShrink: 0,
    }} />
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function CalendarView() {
  const navigate = useNavigate();
  const today    = new Date();

  const [currentDate,  setCurrentDate]  = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [holidaySet,   setHolidaySet]   = useState(new Set());
  const [lockedSet,    setLockedSet]    = useState(new Set());
  const [loadingDates, setLoadingDates] = useState(true);
  const earliestLockableWed            = getEarliestLockableWed(today);

  const [modal,       setModal]       = useState(null);
  const [apiLoading,  setApiLoading]  = useState(false);
  const [toast,       setToast]       = useState({ visible: false, message: "", type: "default" });
  const [shakingCell, setShakingCell] = useState(null);
  const toastTimer = useRef(null);

  const userEmail = localStorage.getItem("email") || "";
  const avatarInitials = userEmail ? userEmail.split("@")[0].slice(0, 2).toUpperCase() : "??";

  const handleLogout = () => {
    localStorage.removeItem("x-cc-auth");
    window.location.href = "/";
  };

  useEffect(() => {
    const token = localStorage.getItem("x-cc-auth");
    if (!token) { navigate("/"); return; }
    fetch(ALL_DATES_API, {
      method: "POST",
      headers: {
        "accept": "*/*", "content-type": "application/json",
        "X-CC-AUTH": `${token}`,
        "origin": "https://admin.chefcoco.com", "referer": "https://admin.chefcoco.com/",
      },
    })
      .then((r) => r.json())
      .then((dates) => {
        if (!Array.isArray(dates)) return;
        const holidays = new Set();
        const locked   = new Set();
        dates.forEach((dateStr) => {
          holidays.add(dateStr);
        });
        setHolidaySet(holidays);
        setLockedSet(locked);
      })
      .catch((e) => console.warn("Could not load dates:", e))
      .finally(() => setLoadingDates(false));
  }, [navigate]);

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay        = new Date(year, month, 1).getDay();
  const daysInMonth     = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  function isHolidayDate(y, m, d) { return holidaySet.has(toKey(y, m, d)); }
  function isLockedDate(y, m, d)  { return lockedSet.has(toKey(y, m, d));  }

  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ day: daysInPrevMonth - i, type: "prev" });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, type: "current" });
  for (let d = 1; d <= 42 - cells.length; d++)
    cells.push({ day: d, type: "next" });

  function showToast(message, type = "default") {
    clearTimeout(toastTimer.current);
    setToast({ visible: true, message, type });
    toastTimer.current = setTimeout(() => setToast({ visible: false, message: "", type: "default" }), 2800);
  }

  function handleDayClick(cell, i, isWeekend, isHoliday, isLocked) {
    if (cell.type !== "current" || isWeekend) return;
    const cellDate = new Date(year, month, cell.day);
    cellDate.setHours(0, 0, 0, 0);
    const key = toKey(year, month, cell.day);

    if (isHoliday || isLocked) {
      setModal({ type: "unblock", date: cellDate, key, isHoliday });
      return;
    }
    if (cellDate < earliestLockableWed) {
      setShakingCell(i);
      setTimeout(() => setShakingCell(null), 500);
      showToast("Past days cannot be blocked");
      return;
    }
    setModal({ type: "block", date: cellDate, key, isHoliday: false });
  }

  async function confirmModal() {
    if (!modal) return;
    const token = localStorage.getItem("x-cc-auth");
    const url   = modal.type === "block" ? BLACKLIST_API : WHITELIST_API;
    setApiLoading(true);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "accept": "*/*", "content-type": "application/json",
          "X-CC-AUTH": `${token}`,
          "origin": "https://admin.chefcoco.com", "referer": "https://admin.chefcoco.com/",
        },
        body: JSON.stringify({ date: modal.key }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (modal.type === "block") {
        setLockedSet((prev) => new Set([...prev, modal.key]));
      } else if (modal.isHoliday) {
        setHolidaySet((prev) => { const n = new Set(prev); n.delete(modal.key); return n; });
      } else {
        setLockedSet((prev) => { const n = new Set(prev); n.delete(modal.key); return n; });
      }
      showToast(
        modal.type === "block" ? "Delivery blocked successfully" : "Block removed successfully",
        "success"
      );
    } catch (err) {
      console.error("API error:", err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setApiLoading(false);
      setModal(null);
    }
  }

  const blockedThisMonth = [...holidaySet].filter((k) => {
    const [y, m] = k.split("-").map(Number);
    return y === year && m === month + 1;
  });

  return (
    <>
      <style>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-5px)} 40%{transform:translateX(5px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        @keyframes lockPop { 0%{transform:scale(1)} 50%{transform:scale(1.3)} 100%{transform:scale(1)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes popIn { from{opacity:0;transform:translate(-50%,-48%) scale(0.92)} to{opacity:1;transform:translate(-50%,-50%) scale(1)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .shake { animation: shake 0.45s ease; }
        .lock-pop { animation: lockPop 0.25s ease; }
      `}</style>

      <div className="dashboard-container">
        <Sidebar
          currentPath="/calendar"
          onNavigate={(path) => navigate(path)}
          onLogout={handleLogout}
        />

        <div className="main-content">
          {/* Topbar */}
          <div className="topbar">
            <div className="topbar-title">
              <h2>Delivery Calendar</h2>
              <span className="topbar-badge">Account · Schedule</span>
            </div>
            <button className="avatar-btn" title={userEmail}>{avatarInitials}</button>
          </div>

          <div className="content-area" style={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
            <div style={{
              background: "#ffffff", borderRadius: "20px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
              width: "400px", overflow: "hidden",
            }}>

              {/* Month nav */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 8px" }}>
                <button onClick={prevMonth} style={navBtn}>‹</button>
                <div>
                  <span style={{ fontWeight: 700, fontSize: "16px", color: "#1a1a1a" }}>{MONTHS[month]}</span>
                  <span style={{ fontWeight: 400, fontSize: "15px", color: "#888", marginLeft: "6px" }}>{year}</span>
                </div>
                <button onClick={nextMonth} style={navBtn}>›</button>
              </div>

              {loadingDates && (
                <div style={{ textAlign: "center", fontSize: "11px", color: "#aaa", padding: "2px 0 8px", animation: "pulse 1.2s ease infinite" }}>
                  Loading schedule…
                </div>
              )}

              {/* Legend */}
              <div style={{ display: "flex", gap: "14px", padding: "0 20px 10px", justifyContent: "center", fontSize: "11px", color: "#555", flexWrap: "wrap" }}>
                {[
                  { color: "#eab308", label: "Today" },
                  { color: "#dc2626", label: "Blocked" },
                ].map(({ color, label }) => (
                  <span key={label} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: color, display: "inline-block" }} />
                    {label}
                  </span>
                ))}
              </div>

              {/* Day headers */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "0 16px", marginBottom: "4px" }}>
                {DAYS.map((d, i) => (
                  <div key={d} style={{
                    textAlign: "center", fontSize: "11px", fontWeight: 700,
                    color: i === 0 || i === 6 ? "#e8372c" : "#aaa",
                    opacity: i === 0 || i === 6 ? 0.45 : 1,
                    padding: "6px 0", textTransform: "uppercase", letterSpacing: "0.5px",
                  }}>
                    {d}
                  </div>
                ))}
              </div>

              {/* Cells */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "0 16px 16px", gap: "4px" }}>
                {cells.map((cell, i) => {
                  const isCurrent = cell.type === "current";
                  const dayOfWeek = i % 7;
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const isHoliday = isCurrent && isHolidayDate(year, month, cell.day);
                  const isLocked  = isCurrent && isLockedDate(year, month, cell.day);
                  const todayCell = isCurrent && !isWeekend && isToday(cell.day);

                  const cellDate = isCurrent
                    ? (() => { const d = new Date(year, month, cell.day); d.setHours(0,0,0,0); return d; })()
                    : null;
                  const isLockableWed   = isCurrent && !isHoliday && cellDate >= earliestLockableWed;
                  const isPastWed       = isCurrent && !isHoliday && cellDate < earliestLockableWed;
                  const isShaking       = shakingCell === i;

                  // Colors: today=yellow, holiday/blocked=red, locked=blue
                  let bgColor = "transparent";
                  if (isCurrent && todayCell)       bgColor = "#eab308";
                  else if (isCurrent && isHoliday)  bgColor = "#dc2626";

                  let textColor = "#ccc";
                  if (isCurrent) {
                    if      (isWeekend)       textColor = "#bbb";
                    else if (todayCell)       textColor = "#fff";
                    else if (isHoliday)       textColor = "#fff";
                    else if (isLockableWed)   textColor = "#1a1a1a";
                    else if (isPastWed)       textColor = "#6b7280";
                    else                      textColor = "#1a1a1a";
                  }

                  const canInteract = isCurrent && !isWeekend && (isHoliday || isLockableWed);

                  return (
                    <div
                      key={i}
                      className={isShaking ? "shake" : ""}
                      onClick={() => handleDayClick(cell, i, isWeekend, isHoliday, isLocked)}
                      style={{
                        width: "40px", height: "40px",
                        textAlign: "center", borderRadius: "50%",
                        fontSize: "13px",
                        fontWeight: todayCell || isHoliday ? 700 : isCurrent ? 600 : 400,
                        cursor: canInteract ? "pointer" : "default",
                        color: textColor, background: bgColor,
                        outline: isLockableWed ? "1.5px dashed #93c5fd" : "none",
                        opacity: isWeekend ? 0.4 : !isCurrent ? 0.3 : 1,
                        transition: "background 0.18s, color 0.18s",
                        userSelect: "none", position: "relative",
                        display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center",
                        gap: "1px", boxSizing: "border-box",
                      }}
                    >
                      <span>{cell.day}</span>
                      {isHoliday && (
                        <span style={{ display: "block", width: "3px", height: "3px", borderRadius: "50%", background: "#fff" }} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "10px" }}>
                  <button onClick={goToToday} style={{
                    background: "none", border: "1.5px solid #e8372c", color: "#e8372c",
                    borderRadius: "8px", padding: "6px 18px", fontSize: "12px",
                    fontWeight: 600, cursor: "pointer", letterSpacing: "0.3px",
                  }}>Today</button>
                </div>
                <p style={{
                  margin: 0, fontSize: "11px", color: "#6b7280",
                  textAlign: "center", lineHeight: 1.5,
                  padding: "8px 12px",
                  background: "#fffbeb",
                  borderRadius: "8px",
                  border: "1px solid #fde68a",
                }}>
                  ⚠️ Please note: Delivery days must be blocked at least 7 days in advance.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>

      <Toast message={toast.message} visible={toast.visible} type={toast.type} />

      {modal?.type === "block" && (
        <BlockModal date={modal.date} onConfirm={confirmModal} onClose={() => !apiLoading && setModal(null)} loading={apiLoading} />
      )}
      {modal?.type === "unblock" && (
        <UnblockModal date={modal.date} isBlocked={modal.isHoliday} onConfirm={confirmModal} onClose={() => !apiLoading && setModal(null)} loading={apiLoading} />
      )}
    </>
  );
}

const navBtn = {
  background: "none", border: "none", fontSize: "22px",
  color: "#555", cursor: "pointer", padding: "4px 10px",
  borderRadius: "8px", lineHeight: 1,
};
